import { Wand2, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'

interface PromptInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  maxLength?: number
  rows?: number
  label?: string
  /** If provided, shows an "Auto Prompt" button that fills with this text */
  autoPromptText?: string
  /** If provided, shows an "Auto Prompt" button that calls this function (takes precedence over autoPromptText) */
  onAutoPrompt?: () => void
  /** Show loading state on the auto-prompt button */
  isAutoPromptLoading?: boolean
  /** Disable auto-prompt button (e.g., when no image is uploaded) */
  autoPromptDisabled?: boolean
  /** Disable the input */
  disabled?: boolean
}

/**
 * Reusable prompt textarea with character counter.
 * Used across all generation forms (T2V, T2I, I2V, Image Edit, Extend Video).
 */
export function PromptInput({
  value,
  onChange,
  placeholder = 'Describe what you want to create...',
  maxLength = 1000,
  rows = 4,
  label,
  autoPromptText,
  onAutoPrompt,
  isAutoPromptLoading = false,
  autoPromptDisabled = false,
  disabled = false,
}: PromptInputProps) {
  const handleChange = (newValue: string) => {
    if (newValue.length <= maxLength) {
      onChange(newValue)
    }
  }

  // Calculate height based on rows (each row ~24px + padding)
  const height = rows === 4 ? 'h-40' : rows === 3 ? 'h-32' : 'h-28'
  
  const showAutoPrompt = onAutoPrompt || autoPromptText
  
  const handleAutoPrompt = () => {
    if (onAutoPrompt) {
      onAutoPrompt()
    } else if (autoPromptText) {
      onChange(autoPromptText)
    }
  }

  return (
    <div className="space-y-3">
      {label && (
        <label className="text-sm font-medium text-[#888]">{label}</label>
      )}
      <div className="relative">
        <textarea
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "w-full px-4 py-4 pb-8 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white placeholder:text-[#888] resize-none focus:outline-none focus:border-white/[0.2] transition-colors",
            height,
            showAutoPrompt && "pr-28",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        />
        {showAutoPrompt && !value && (
          <button
            type="button"
            onClick={handleAutoPrompt}
            disabled={autoPromptDisabled || isAutoPromptLoading}
            title={autoPromptDisabled ? 'Select an image first' : 'Generate prompt from image'}
            className={cn(
              "absolute right-3 top-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1a1a1a] border border-white/[0.1] text-xs transition-colors overflow-hidden",
              autoPromptDisabled || isAutoPromptLoading
                ? "text-[#555] cursor-not-allowed"
                : "text-[#888] hover:text-white hover:bg-[#222]"
            )}
          >
            {/* Loading bar */}
            {isAutoPromptLoading && (
              <div className="absolute top-0 left-0 h-[2px] bg-white/80 animate-loader-progress" />
            )}
            {isAutoPromptLoading ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Wand2 className="size-3.5" />
                Auto Prompt
              </>
            )}
          </button>
        )}
        <span className={cn(
          "absolute right-3 bottom-3 text-xs",
          value.length >= maxLength ? "text-red-400" : "text-[#555]"
        )}>
          {value.length}/{maxLength}
        </span>
      </div>
    </div>
  )
}
